import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { patientService } from '@/services/patientService';
import { Patient } from '@/types/patient.types';
import { useToast } from '@/hooks/use-toast';
import { UserPlus, X, AlertTriangle, CheckCircle } from 'lucide-react';

interface TakeoverPatientDialogProps {
  isOpen: boolean;
  patient: Patient | null;
  onClose: () => void;
  onSuccess: (patient: Patient) => void;
}

export const TakeoverPatientDialog: React.FC<TakeoverPatientDialogProps> = ({
  isOpen,
  patient,
  onClose,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [cnp, setCnp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Reset state when dialog closes
  const handleClose = () => {
    setCnp('');
    setError(null);
    setShowConfirmation(false);
    setIsLoading(false);
    onClose();
  };

  // Handle CNP input change
  const handleCnpChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 13) {
      setCnp(numericValue);
      setError(null);
    }
  };

  // Validate CNP format (basic validation)
  const validateCnp = (cnpValue: string): boolean => {
    if (cnpValue.length !== 13) {
      setError('CNP-ul trebuie să conțină exact 13 cifre');
      return false;
    }
    return true;
  };

  // Handle takeover submission
  const handleTakeover = async () => {
    if (!patient) return;

    if (!validateCnp(cnp)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await patientService.takeoverPatient(patient.id, cnp);

      toast({
        title: 'Pacient preluat cu succes',
        description: result.message || `Pacientul ${patient.name} a fost preluat de spitalul dvs.`,
        variant: 'default',
      });

      onSuccess(result.patient);
      handleClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Nu s-a putut prelua pacientul. Verificați CNP-ul și încercați din nou.';
      setError(errorMessage);

      toast({
        title: 'Eroare la preluarea pacientului',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle verification before takeover
  const handleVerify = () => {
    if (validateCnp(cnp)) {
      setShowConfirmation(true);
    }
  };

  if (!patient) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Preia pacient
          </DialogTitle>
          <DialogDescription>
            Preluarea unui pacient îl va transfera în gestiunea spitalului dvs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Patient information */}
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <div>
              <span className="text-sm font-medium text-muted-foreground">Pacient:</span>
              <p className="text-sm font-semibold">{patient.name}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-muted-foreground">Cod pacient:</span>
              <p className="text-sm font-semibold">{patient.patient_code}</p>
            </div>
            {patient.hospital_name && (
              <div>
                <span className="text-sm font-medium text-muted-foreground">Spital actual:</span>
                <p className="text-sm">{patient.hospital_name}</p>
              </div>
            )}
          </div>

          {/* Confirmation warning */}
          {!showConfirmation ? (
            <>
              {/* CNP input */}
              <div className="space-y-2">
                <Label htmlFor="cnp">
                  CNP pacient <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="cnp"
                  type="text"
                  placeholder="Introduceți CNP-ul pacientului (13 cifre)"
                  value={cnp}
                  onChange={(e) => handleCnpChange(e.target.value)}
                  maxLength={13}
                  disabled={isLoading}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Pentru securitate, introduceți CNP-ul pacientului pentru a confirma identitatea.
                </p>
              </div>

              {/* Info message */}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Atenție:</strong> Preluând acest pacient, datele acestuia vor fi transferate în gestiunea spitalului dvs.
                  Veți putea edita și actualiza informațiile pacientului după preluare.
                </AlertDescription>
              </Alert>
            </>
          ) : (
            <>
              {/* Confirmation message */}
              <Alert className="border-orange-500 bg-orange-50">
                <CheckCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-sm">
                  <strong>Confirmați preluarea:</strong>
                  <br />
                  Sunteți pe cale să preluați pacientul <strong>{patient.name}</strong> (CNP: {cnp.substring(0, 6)}***).
                  <br />
                  <br />
                  Această acțiune va transfera responsabilitatea pacientului către spitalul dvs.
                </AlertDescription>
              </Alert>
            </>
          )}

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            <X className="mr-2 h-4 w-4" />
            Anulează
          </Button>

          {!showConfirmation ? (
            <Button
              onClick={handleVerify}
              disabled={isLoading || cnp.length !== 13}
            >
              Continuă
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
                disabled={isLoading}
              >
                Înapoi
              </Button>
              <Button
                onClick={handleTakeover}
                disabled={isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Se preia...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Preia pacient
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
